import React, { useCallback, useMemo } from 'react';
import { Box } from '@atlaskit/primitives/compiled';
import { isFedrampModerate } from '@atlaskit/atlassian-context';
import { withCompassEntitlementCheck } from '@atlassian/jira-compass-cross-flow/src/services/compass-tcs-check/index.tsx';
import { AsyncCompassTableCrossFlowTouchPoint } from '@atlassian/jira-compass-cross-flow/src/ui/compass-below-table-touchpoint/index.tsx';
import { CrossFlowProvider } from '@atlassian/jira-cross-flow/src/services/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useCompassComponentsSpotlightViewed } from '../../common/services/use-compass-components-spotlight-viewed/use-compass-components-spotlight-viewed.tsx';
import { useTouchPointDismissed } from './use-touchpoint-viewed/index.tsx';

type Props = {
	isCompassLicensed: boolean;
	origin: string;
};

const CompassTableCrossflowTouchPoint = (props: Props) => {
	const { isCompassLicensed, origin } = props;
	const [{ hasBeenDismissed }, { dismiss }] = useTouchPointDismissed();
	const isFedRamp = fg('hide_fedramp_compass_component_in_jira') && isFedrampModerate();

	const [{ hasBeenViewed: hasSpotlightBeenViewed }] = useCompassComponentsSpotlightViewed();

	const onDismiss = useCallback(async () => {
		dismiss();
	}, [dismiss]);

	// Conditions for when we should render the touch point.
	const shouldRender = useMemo(() => {
		if (isFedRamp) {
			// Don't render the touch point if the customer is the FedRamp user.
			return false;
		}
		if (isCompassLicensed) {
			// Don't render the touch point if Compass is already provisioned.
			return false;
		}

		if (!hasSpotlightBeenViewed) {
			// Don't render the touch point until user has dismissed our first spotlight.
			return false;
		}

		// Don't render the touch point if a user has already dismissed it.
		return !hasBeenDismissed;
	}, [isFedRamp, isCompassLicensed, hasSpotlightBeenViewed, hasBeenDismissed]);

	return shouldRender ? (
		<CrossFlowProvider>
			<Box paddingBlockEnd="space.300">
				<AsyncCompassTableCrossFlowTouchPoint origin={origin} onDismiss={onDismiss} />
			</Box>
		</CrossFlowProvider>
	) : null;
};

export const CompassTableCrossflowTouchPointWithEC = withCompassEntitlementCheck(
	CompassTableCrossflowTouchPoint,
	'project-pages.project-directories.components.view.table',
);
